import { Box, Typography } from '@mui/material';

import {
  MarketingCtaBand,
  MarketingFeatureGrid,
  MarketingHero,
} from '@/components/Marketing/blocks';
import { MarketingPageShell } from '@/components/Marketing/MarketingPageShell';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function DscrPage() {
  usePageMeta(
    'Unbeatable DSCR',
    'DSCR loans for rental and investment properties—qualify from property income.'
  );
  return (
    <MarketingPageShell
      title="Unbeatable DSCR"
      subtitle="Business-purpose mortgages for rental and investment real estate nationwide."
    >
      <MarketingHero
        eyebrow="Meet the Unbeatable DSCR"
        title="Better, Faster, Easier."
        description="We didn't invent the DSCR mortgage—we do them with investor-first workflow, transparent estimates, and support that speaks your language."
      />

      <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
        Unbeatable DSCR loans are designed for real estate investment across the
        USA. Qualification is based on property income—not personal pay stubs.
        Popular with business owners building rental portfolios.
      </Typography>

      <MarketingFeatureGrid
        items={[
          {
            title: 'Qualification based on property income',
            body: 'Underwrite from rents and operating numbers, not W-2s.',
          },
          {
            title: 'No personal income verification required',
            body: 'True investor workflow for qualifying deals.',
          },
          {
            title: 'Popular choice for business owners',
            body: 'LLCs and investment entities welcome where program allows.',
          },
        ]}
      />

      <Typography variant="h5" sx={{ fontWeight: 800, mt: 5, mb: 2 }}>
        Unbeatable DSCR is perfect for
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 4,
        }}
      >
        {[
          'Business owners',
          'Real estate investors',
          'Short-term rentals',
          'Cash flow',
          'Diversification',
          'Generational wealth',
          'Vacation rentals',
          '1–4 unit properties',
          'Student housing',
        ].map((label) => (
          <Box
            key={label}
            sx={{
              px: 2,
              py: 0.75,
              borderRadius: 999,
              bgcolor: 'action.hover',
              typography: 'body2',
              fontWeight: 600,
            }}
          >
            {label}
          </Box>
        ))}
      </Box>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Investors don't use buyers agents
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3, lineHeight: 1.8 }}
      >
        Skip the agent: maximize ROI with our technology to submit and negotiate
        offers. No-income verification means we qualify you from rental
        income—fewer conditions and faster paths to close. LTV up to 90% on
        eligible programs so you preserve capital for reserves and the next
        deal.
      </Typography>

      <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
        What makes our DSCR Unbeatable
      </Typography>
      <MarketingFeatureGrid
        columns={{ xs: 12, md: 6 }}
        items={[
          {
            title: 'No cap on properties owned',
            body: 'Grow your portfolio without arbitrary limits on how many homes you finance.',
          },
          {
            title: 'Fast approvals',
            body: 'Streamlined flow built for investors who move quickly on opportunities.',
          },
          {
            title: 'True no-income loans',
            body: 'No tax returns or pay stubs—property cash flow drives the credit decision.',
          },
          {
            title: 'Custom rate structures',
            body: 'Credits, interest-only, and prepay options matched to your strategy.',
          },
          {
            title: 'Skip the real estate agent',
            body: 'Submit offers without buyer-agent commissions where you choose to.',
          },
          {
            title: 'Custom support',
            body: 'A team that understands investment math—not generic bank scripts.',
          },
        ]}
      />

      <MarketingCtaBand
        title="Experience Unbeatable Loans yourself"
        subtitle="Explore DSCR loan estimates and submit offers from the app."
        href="https://app.unbeatableloans.com/"
        buttonLabel="Open the app"
        external
      />
    </MarketingPageShell>
  );
}
